"use client";

// Lazy client entry points for the storefront's server components.
//
// Next (Turbopack) ships a route segment's client code as ONE group: every
// "use client" module reachable from the segment's server graph, whether the
// page renders it or not — static imports, `await import()` and a server-side
// next/dynamic all land in that group. The storefront layout reaches every
// widget through the header/footer resolver, so every page downloaded every
// widget's client code: the product listing and its filters, countdowns,
// forms, the classic header's drawers…
//
// A next/dynamic import inside a CLIENT module is the split point Turbopack
// honours: the component moves to its own chunk, fetched only when rendered.
// With ssr on (the default) the server HTML is unchanged, and a rendered
// component's chunk is still requested while the page streams.
//
// Server components import these client components from here, not directly;
// lazy-client.test.ts enforces that for the widget tree and the classic
// header/footer.

import dynamic from "next/dynamic";

// ── Structured widgets ──────────────────────────────────────
export const AnnouncementClient = dynamic(() =>
  import("./structured-widgets/content/AnnouncementClient").then(
    (m) => m.AnnouncementClient,
  ),
);
export const PcBuilderView = dynamic(() =>
  import("./structured-widgets/content/PcBuilderView").then(
    (m) => m.PcBuilderView,
  ),
);

export const RigHeroView = dynamic(() =>
  import("./structured-widgets/content/RigHeroView").then(
    (m) => m.RigHeroView,
  ),
);

export const StockTickerView = dynamic(() =>
  import("./structured-widgets/content/StockTickerView").then(
    (m) => m.StockTickerView,
  ),
);

export const ShopTheRoomView = dynamic(() =>
  import("./structured-widgets/content/ShopTheRoomView").then(
    (m) => m.ShopTheRoomView,
  ),
);
export const BrandLogosWidget = dynamic(() =>
  import("./structured-widgets/content/BrandLogosWidget").then(
    (m) => m.BrandLogosWidget,
  ),
);
export const ContactFormClient = dynamic(() =>
  import("./structured-widgets/content/ContactFormClient").then(
    (m) => m.ContactFormClient,
  ),
);
export const ContactInfoWidget = dynamic(() =>
  import("./structured-widgets/content/ContactInfoWidget").then(
    (m) => m.ContactInfoWidget,
  ),
);
export const CountdownClient = dynamic(() =>
  import("./structured-widgets/content/CountdownClient").then(
    (m) => m.CountdownClient,
  ),
);
export const DiscountCodeClient = dynamic(() =>
  import("./structured-widgets/content/DiscountCodeClient").then(
    (m) => m.DiscountCodeClient,
  ),
);
export const FaqWidgetClient = dynamic(() =>
  import("./structured-widgets/content/FaqWidgetClient").then(
    (m) => m.FaqWidgetClient,
  ),
);
export const HeroBannerClient = dynamic(() =>
  import("./structured-widgets/content/HeroBannerClient").then(
    (m) => m.HeroBannerClient,
  ),
);
export const MarqueeClient = dynamic(() =>
  import("./structured-widgets/content/MarqueeClient").then(
    (m) => m.MarqueeClient,
  ),
);
export const NewsletterClient = dynamic(() =>
  import("./structured-widgets/content/NewsletterClient").then(
    (m) => m.NewsletterClient,
  ),
);
export const PaymentMethodsWidget = dynamic(() =>
  import("./structured-widgets/content/PaymentMethodsWidget").then(
    (m) => m.PaymentMethodsWidget,
  ),
);
export const PromoBannerClient = dynamic(() =>
  import("./structured-widgets/content/PromoBannerClient").then(
    (m) => m.PromoBannerClient,
  ),
);
export const RecentOrdersClient = dynamic(() =>
  import("./structured-widgets/content/RecentOrdersClient").then(
    (m) => m.RecentOrdersClient,
  ),
);
export const SocialIconsWidget = dynamic(() =>
  import("./structured-widgets/content/SocialIconsWidget").then(
    (m) => m.SocialIconsWidget,
  ),
);
export const TabsClient = dynamic(() =>
  import("./structured-widgets/content/TabsClient").then((m) => m.TabsClient),
);
export const TestimonialsClient = dynamic(() =>
  import("./structured-widgets/content/TestimonialsClient").then(
    (m) => m.TestimonialsClient,
  ),
);
export const CategoryCard = dynamic(() =>
  import("./structured-widgets/ecommerce/CategoryCard").then(
    (m) => m.CategoryCard,
  ),
);
export const FeaturedProductAddToCart = dynamic(() =>
  import("./structured-widgets/ecommerce/FeaturedProductAddToCart").then(
    (m) => m.FeaturedProductAddToCart,
  ),
);
export const FreeShippingBarClient = dynamic(() =>
  import("./structured-widgets/ecommerce/FreeShippingBarClient").then(
    (m) => m.FreeShippingBarClient,
  ),
);
export const ProductCard = dynamic(() =>
  import("./structured-widgets/ecommerce/ProductCard").then(
    (m) => m.ProductCard,
  ),
);
export const RecentlyViewedClient = dynamic(() =>
  import("./structured-widgets/ecommerce/RecentlyViewedClient").then(
    (m) => m.RecentlyViewedClient,
  ),
);
export const HeaderIconsWidget = dynamic(() =>
  import("./structured-widgets/navigation/HeaderIconsWidget").then(
    (m) => m.HeaderIconsWidget,
  ),
);
export const NavBarWidget = dynamic(() =>
  import("./structured-widgets/navigation/NavBarWidget").then(
    (m) => m.NavBarWidget,
  ),
);
export const ProductListLayout = dynamic(() => import("./ProductListLayout"));

// ── Classic header / footer (tenants without a structured header) ──
export const CartIcon = dynamic(() => import("./CartIcon"));
export const CurrencySwitcher = dynamic(() => import("./CurrencySwitcher"));
export const MegaMenuNav = dynamic(() =>
  import("./MegaMenuNav").then((m) => m.MegaMenuNav),
);
export const MobileNavDrawer = dynamic(() => import("./MobileNavDrawer"));
export const WishlistIcon = dynamic(() => import("./WishlistIcon"));
export const HeaderHeightPublisher = dynamic(() =>
  import("./HeaderHeightPublisher").then((m) => m.HeaderHeightPublisher),
);
export const LocaleSwitcher = dynamic(
  () => import("@/components/layout/LocaleSwitcher"),
);
export const UserMenu = dynamic(() => import("@/components/layout/UserMenu"));
export const ClassicFooter = dynamic(() => import("@/components/layout/Footer"));
